import { useModalLifecycle } from '../shared/useModalLifecycle'
/**
 * Team Spain v0 — Modal de Comunicación de Incidencia o Molestia (M6)
 * 
 * Permite comunicar una molestia o lesión en cualquier momento (sin esperar al semanal):
 * - Admite reportar una nueva incidencia o añadir una actualización ligada a un episodio existente.
 * - 17 zonas anatómicas canónicas y lateralidad explícita.
 * - Escala de dolor 0-10, efecto en la actividad y disponibilidad declarada.
 * - Solicitud explícita de contacto con el servicio médico.
 */

import React, { useState } from 'react'
import { X, Activity, AlertTriangle, Send } from 'lucide-react'
import { formsService } from './formsService'
import {
  BodyZone,
  Laterality,
  DeclaredAvailability,
  ActivityEffect,
  HealthIncidentItem,
} from '../../types/health'

export const BODY_ZONES_CONFIG: Array<{ id: BodyZone; label: string }> = [
  { id: 'head', label: 'Cabeza / Cara' },
  { id: 'neck', label: 'Cuello / Cervicales' },
  { id: 'shoulder', label: 'Hombro / Clavícula' },
  { id: 'arm', label: 'Brazo / Bíceps / Tríceps' },
  { id: 'elbow', label: 'Codo' },
  { id: 'wrist_hand', label: 'Muñeca / Mano / Dedos' },
  { id: 'chest', label: 'Pecho / Costillas' },
  { id: 'upper_back', label: 'Espalda alta / Dorsales' },
  { id: 'lower_back', label: 'Espalda baja / Lumbar' },
  { id: 'hip_groin', label: 'Cadera / Ingle / Aductor' },
  { id: 'thigh_quad', label: 'Muslo anterior / Cuádriceps' },
  { id: 'thigh_hamstring', label: 'Muslo posterior / Isquiotibiales' },
  { id: 'knee', label: 'Rodilla' },
  { id: 'lower_leg_shin', label: 'Pierna anterior / Espinilla' },
  { id: 'calf', label: 'Gemelo / Sóleo' },
  { id: 'ankle', label: 'Tobillo' },
  { id: 'foot_toes', label: 'Pie / Dedos / Fascia plantar' },
]

interface IncidentReportModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  teamId: string
  athleteId: string
  openEpisodes?: HealthIncidentItem[]
}

export const IncidentReportModal: React.FC<IncidentReportModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  teamId,
  athleteId,
  openEpisodes = [],
}) => {
  const [reportType, setReportType] = useState<'new' | 'followup'>(
    openEpisodes.length > 0 ? 'new' : 'new'
  )
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string>(
    openEpisodes[0]?.id || ''
  )
  const [incidentDate, setIncidentDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  )
  const [context, setContext] = useState<'training' | 'competition' | 'other'>('training')
  const [bodyZone, setBodyZone] = useState<BodyZone>('thigh_hamstring')
  const [laterality, setLaterality] = useState<Laterality>('right')
  const [painLevel, setPainLevel] = useState<number>(4)
  const [description, setDescription] = useState<string>('')
  const [activityEffect, setActivityEffect] = useState<ActivityEffect>('normal_with_pain')
  const [professionalCare, setProfessionalCare] = useState<string>('')
  const [declaredAvailability, setDeclaredAvailability] = useState<DeclaredAvailability>('limited')
  const [contactRequested, setContactRequested] = useState<boolean>(true)
  const [contactComment, setContactComment] = useState<string>('')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const isDirty =
    description.trim() !== '' ||
    professionalCare.trim() !== '' ||
    contactComment.trim() !== '' ||
    painLevel !== 4

  const handleCloseAttempt = () => {
    if (isDirty) {
      const confirmLeave = window.confirm(
        'Tienes datos introducidos en la comunicación de molestia sin enviar. ¿Deseas descartarlos y salir?'
      )
      if (!confirmLeave) return
    }
    onClose()
  }

  useModalLifecycle(isOpen, handleCloseAttempt)

  if (!isOpen) return null

  // Si seleccionó actualización de episodio existente, auto-completar zona y lado
  const handleSelectEpisode = (epId: string) => {
    setSelectedEpisodeId(epId)
    const ep = openEpisodes.find((e) => e.id === epId)
    if (ep) {
      setBodyZone(ep.body_zone)
      setLaterality(ep.laterality || 'right')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)

    if (!description.trim()) {
      setErrorMsg('Por favor describe brevemente la molestia o sensaciones.')
      return
    }

    setIsSubmitting(true)
    try {
      await formsService.createHealthIncident({
        team_id: teamId,
        athlete_id: athleteId,
        episode_id: reportType === 'followup' ? selectedEpisodeId : null,
        incident_date: incidentDate,
        context,
        body_zone: bodyZone,
        laterality,
        pain_level: painLevel,
        description: description.trim(),
        activity_effect: activityEffect,
        professional_care: professionalCare.trim() || null,
        declared_availability: declaredAvailability,
        contact_requested: contactRequested,
        contact_comment: contactRequested ? contactComment.trim() : null,
        status: 'open',
        is_active: true,
      })

      onSuccess()
      onClose()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al comunicar la molestia.'
      setErrorMsg(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="mobile-viewport-overlay health-form-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCloseAttempt()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 19, 32, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
        padding: '1rem',
      }}
    >
      <div
        className="mobile-viewport-sheet"
        style={{
          backgroundColor: 'var(--surface-card)',
          borderRadius: '12px',
          padding: '1.5rem',
          maxWidth: '540px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-card)',
          border: '1px solid var(--surface-border)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: 'rgba(239, 68, 68, 0.12)',
                color: 'var(--brand-red)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Activity size={18} />
            </div>
            <div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
                Comunicar molestia o incidencia
              </h2>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Disponible bajo demanda para avisar al servicio médico
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCloseAttempt}
            aria-label="Cerrar modal"
            style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        {errorMsg && (
          <div
            style={{
              padding: '0.75rem',
              backgroundColor: 'var(--status-danger-bg)',
              color: 'var(--status-danger-text)',
              borderRadius: '8px',
              fontSize: '0.82rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1rem',
            }}
          >
            <AlertTriangle size={16} />
            <span>{errorMsg}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Tipo de reporte: Nueva vs Actualización de episodio existente */}
          {openEpisodes.length > 0 && (
            <div
              style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                backgroundColor: 'var(--surface-sunken)',
                borderRadius: '8px',
                border: '1px solid var(--surface-border)',
              }}
            >
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, marginBottom: '0.4rem' }}>
                ¿Es una molestia nueva o seguimiento de un caso previo?
              </label>
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="reportType"
                    checked={reportType === 'new'}
                    onChange={() => setReportType('new')}
                  />
                  <span>Nueva molestia</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="reportType"
                    checked={reportType === 'followup'}
                    onChange={() => setReportType('followup')}
                  />
                  <span>Actualización de episodio previo</span>
                </label>
              </div>

              {reportType === 'followup' && (
                <div style={{ marginTop: '0.5rem' }}>
                  <label style={{ display: 'block', fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>
                    Selecciona el episodio a actualizar:
                  </label>
                  <select
                    value={selectedEpisodeId}
                    onChange={(e) => handleSelectEpisode(e.target.value)}
                    className="input"
                    style={{ width: '100%', fontSize: '0.82rem' }}
                  >
                    {openEpisodes.map((ep) => (
                      <option key={ep.id} value={ep.id}>
                        {ep.incident_date} · {ep.body_zone} ({ep.laterality || 'N/A'}) - {ep.description?.substring(0, 40)}...
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Fecha y Contexto */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Fecha de la molestia *
              </label>
              <input
                type="date"
                required
                value={incidentDate}
                onChange={(e) => setIncidentDate(e.target.value)}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Contexto en que ocurrió *
              </label>
              <select
                value={context}
                onChange={(e) => setContext(e.target.value as 'training' | 'competition' | 'other')}
                className="input"
                style={{ width: '100%' }}
              >
                <option value="training">Entrenamiento</option>
                <option value="competition">Partido / Competición</option>
                <option value="other">Actividad diaria / Otro</option>
              </select>
            </div>
          </div>

          {/* Zona Anatómica y Lado */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Zona anatómica *
              </label>
              <select
                value={bodyZone}
                onChange={(e) => setBodyZone(e.target.value as BodyZone)}
                className="input"
                style={{ width: '100%' }}
              >
                {BODY_ZONES_CONFIG.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Lateralidad *
              </label>
              <select
                value={laterality}
                onChange={(e) => setLaterality(e.target.value as Laterality)}
                className="input"
                style={{ width: '100%' }}
              >
                <option value="right">Derecho</option>
                <option value="left">Izquierdo</option>
                <option value="bilateral">Bilateral</option>
                <option value="central">Central</option>
                <option value="na">No aplica</option>
              </select>
            </div>
          </div>

          {/* Nivel de Dolor (0 a 10) */}
          <div style={{ marginBottom: '1rem', backgroundColor: 'var(--surface-sunken)', padding: '0.75rem', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 700 }}>
                Intensidad del dolor (0–10)
              </label>
              <span
                style={{
                  fontSize: '1rem',
                  fontWeight: 800,
                  fontFamily: 'monospace',
                  color: painLevel >= 7 ? 'var(--brand-red)' : painLevel >= 4 ? 'var(--brand-yellow)' : 'var(--status-success-text)',
                }}
              >
                {painLevel} / 10
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              step="1"
              value={painLevel}
              onChange={(e) => setPainLevel(Number(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--brand-red)' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
              <span>0: Sin dolor</span>
              <span>5: Dolor moderado</span>
              <span>10: Dolor extremo</span>
            </div>
          </div>

          {/* Descripción */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
              ¿Qué sensaciones tienes o cómo ocurrió? *
            </label>
            <textarea
              rows={3}
              required
              placeholder="Sensación de pinchazo, sobrecarga al acelerar, molestia al rotar..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input"
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>

          {/* Efecto en la Actividad */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
              Efecto en tus entrenamientos / partidos *
            </label>
            <select
              value={activityEffect}
              onChange={(e) => setActivityEffect(e.target.value as ActivityEffect)}
              className="input"
              style={{ width: '100%' }}
            >
              <option value="none">Sin efecto (actividad habitual normal)</option>
              <option value="normal_with_pain">Entreno normal pero con molestias</option>
              <option value="modified">Actividad modificada (evité sprints, saltos o cortes)</option>
              <option value="missed_sessions">Sesiones perdidas (tuve que parar en alguna sesión)</option>
              <option value="did_not_train">No entrené (incapacidad para realizar la sesión)</option>
            </select>
          </div>

          {/* Atención recibida / Tratamiento */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
              Atención recibida o tratamiento realizado (opcional)
            </label>
            <input
              type="text"
              placeholder="Ej. Fisioterapia, hielo/descanso, analgésico pautado..."
              value={professionalCare}
              onChange={(e) => setProfessionalCare(e.target.value)}
              className="input"
              style={{ width: '100%', fontSize: '0.82rem' }}
            />
          </div>

          {/* Disponibilidad Declarada por el Jugador */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
              Tu autopercepción de disponibilidad actual *
            </label>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
              Esta es tu percepción subjetiva; la valoración operativa de campo la emitirá el médico.
            </div>
            <select
              value={declaredAvailability}
              onChange={(e) => setDeclaredAvailability(e.target.value as DeclaredAvailability)}
              className="input"
              style={{ width: '100%' }}
            >
              <option value="available_100">100% disponible (puedo entrenar y jugar)</option>
              <option value="limited">Disponible con limitaciones (requiero adaptar ejercicios)</option>
              <option value="needs_assessment">Solicito valoración médica antes de entrenar</option>
              <option value="unavailable">No disponible para entrenar</option>
            </select>
          </div>

          {/* Solicitud de Contacto con Sanitario */}
          <div
            style={{
              marginBottom: '1.25rem',
              padding: '0.75rem',
              backgroundColor: contactRequested ? 'rgba(59, 130, 246, 0.08)' : 'var(--surface-sunken)',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: contactRequested ? 'var(--brand-blue, #3b82f6)' : 'var(--surface-border)',
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
              <input
                type="checkbox"
                checked={contactRequested}
                onChange={(e) => setContactRequested(e.target.checked)}
              />
              <span>Deseo que el médico / fisioterapeuta contacte conmigo</span>
            </label>

            {contactRequested && (
              <div style={{ marginTop: '0.5rem' }}>
                <input
                  type="text"
                  placeholder="Comentario adicional para el fisio (disponibilidad horaria, preferencia...)"
                  value={contactComment}
                  onChange={(e) => setContactComment(e.target.value)}
                  className="input"
                  style={{ width: '100%', fontSize: '0.8rem' }}
                />
              </div>
            )}
          </div>

          {/* Botones de acción */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              <Send size={15} style={{ marginRight: '0.35rem' }} />
              {isSubmitting ? 'Enviando...' : 'Comunicar incidencia'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
